import { Alert, Button, Checkbox, Col, Divider, Form, Input, Modal, Radio, Row, Select, Tag, Typography } from 'antd'
import {
  BOOKING_PERIOD_HOURS,
  getBookingScheduleInfo,
  getMaxBookingTime,
  getNames,
  getStartTimeAsDate,
  isBallMachineAvailable,
  isBallMachineBooked,
  isEventAdminUser,
  isLastMinute,
  mayBeEdited,
  userIsBookingOwner,
} from '../../util/bookingUtil'
import {
  add,
  differenceInMinutes,
  getDate,
  getHours,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isDate,
  isEqual,
  isSameDay,
  parseISO,
  set,
} from 'date-fns'
import { formatDateTime, parseDateTime } from '../../util/dateTimeUtil'
import { useContext, useState } from 'react'

import AppContext from '../common/AppContext'
import { CaretDownOutlined as CollapseIcon } from '@ant-design/icons'
import CustomDatePicker from '../common/CustomDatePicker'
import { CaretRightOutlined as ExpandIcon } from '@ant-design/icons'
import _ from 'lodash'
import { getServerDate } from '../../util/serverTimeUtil'
import { getTagColour } from './Booking'
import { notifyError } from '../../util/notifyUtil'
import { useEffect } from 'react'
import { useForm } from 'antd/lib/form/Form'
import useResponsive from '../../hooks/useResponsive'
import useUserDirectory from '../../hooks/api/useUserDirectory'

const { Text, Paragraph, Title } = Typography

const REPEAT_NONE = 'none'
const REPEAT_WEEKLY = 'weekly'
const REPEAT_DAILY = 'daily'
const REPEAT_DAILY_WEEKDAYS = 'daily_weekday'

const PlayerInput = ({ num, readonly, loading, users }) => {
  return (
    <>
      <Col xs={20} md={21}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues[`player${num}IsGuest`] !== currentValues[`player${num}IsGuest`]
          }
        >
          {({ getFieldValue }) =>
            getFieldValue([`player${num}IsGuest`]) ? (
              <>
                <Form.Item shouldUpdate label={`Player ${num}`} name={`guest${num}`}>
                  <Input disabled={readonly} placeholder="Enter guest's full name" />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item shouldUpdate label={`Player ${num}`} name={[`player${num}`, 'id']}>
                  <Select
                    disabled={readonly}
                    placeholder="Select player (type to search)"
                    notFoundContent="Not found"
                    allowClear
                    showSearch
                    loading={loading}
                    options={_.map(users, (u) => {
                      const { firstName, lastName } = getNames(u)
                      return {
                        label: `${lastName}, ${firstName}`,
                        value: u.id,
                      }
                    })}
                    optionFilterProp="label"
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                    }
                  />
                </Form.Item>
              </>
            )
          }
        </Form.Item>
      </Col>
      <Col xs={4} md={3}>
        <Form.Item style={{ maxWidth: '10em' }} label="Guest?" name={`player${num}IsGuest`} valuePropName="checked">
          <Checkbox disabled={readonly} />
        </Form.Item>
      </Col>
    </>
  )
}

const BookingModal = ({
  court,
  bookingTime,
  bookingDuration,
  bookingToEdit,
  visible,
  onOk,
  onCancel,
  onDelete,
  onOpenDifferentBooking,
  maxBookingTimeMinutes,
  bookingTimeRemaining,
  bookings,
  readonly: readonlyProp = true,
}) => {
  const { currentUser } = useContext(AppContext)
  const isNew = _.isNil(bookingToEdit?.id)
  const [readonly, setReadonly] = useState(true)
  const [form] = Form.useForm()
  const bp = useResponsive()
  let { users, loading } = useUserDirectory(currentUser)
  const [isProgramOrEvent, setProgramOrEvent] = useState(false)
  const [adminOptionsVisible, setAdminOptionsVisible] = useState(false)
  const [ballMachineAvailable, setBallMachineAvailable] = useState(false)
  const [ballMachineBooked, setBallMachineBooked] = useState(false)
  const [ballMachineNote, setBallMachineNote] = useState(false)
  const [ballMachineChecked, setBallMachineChecked] = useState(false)
  const [isSaving, setSaving] = useState(false)
  const [isMatch, setMatch] = useState(false)
  const [isCurrentTimeslotBookedForBallMachine, setCurrentTimeslotBookedForBallMachine] = useState(false)
  const [numPlayers, setNumPlayers] = useState(1)
  const [endTime, setEndTime] = useState(null)

  const DEFAULT_DURATION = bookingDuration

  const handleEdit = () => {
    setReadonly(false)
  }

  if (bookingToEdit?.parent) {
    bookingToEdit.parent.courts = bookingToEdit.courts
  }

  const updateIsProgramOrEvent = (checked) => {
    setProgramOrEvent(checked)
    setAdminOptionsVisible(checked)
    if (!checked) {
      // Shift the description back into the comment.
      const description = form.getFieldValue('description')
      form.setFieldsValue({ showOwner: true, bookingType: 'General', comment: description, description: null })
      // } else {
      //   // Shift the comment back into the description.
      //   const comment = form.getFieldValue('comment')
      //   form.setFieldsValue({ showOwner: false, bookingType: 'Blocked', description: comment, comment: null })
    }
  }

  const findOverlappingCourt = (startTime, endTime) => {
    const newCourtIds = [court]
    // Check if the start/end times overlap with any existing bookings (with the same court)
    return _.find(bookings, (ex) => {
      const timeOverlaps =
        isBefore(new Date(startTime), new Date(ex.endTime)) &&
        (isAfter(new Date(endTime), new Date(ex.startTime)) || isEqual(new Date(endTime), new Date(ex.startTime)))

      if (!timeOverlaps) return false

      const existingCourtIds = _.map(ex.courts, 'id')
      const sharedCourts = _.intersection(newCourtIds, existingCourtIds)

      // It's a conflict only if they share at least one court during that time
      return sharedCourts.length > 0
    })
  }

  bookingTime = bookingToEdit ? getStartTimeAsDate(bookingToEdit) : bookingTime
  const { END_HOUR, SLOT_DURATION } = getBookingScheduleInfo(bookingTime)

  const isLastMinuteBooking = isNew && isDate(bookingTime) ? isLastMinute(bookingTime) : false
  bookingTimeRemaining = isLastMinuteBooking ? maxBookingTimeMinutes : bookingTimeRemaining
  const maxBookingTime = isNew
    ? getMaxBookingTime(currentUser, maxBookingTimeMinutes, bookingTimeRemaining, isLastMinuteBooking, bookingTime)
    : maxBookingTimeMinutes

  const allowedEndTimes = []
  for (let i = 1; i < maxBookingTime; i++) {
    const time = add(bookingTime, { minutes: (i - 1) * SLOT_DURATION })
    const fallsWithinLastMinBookingWindow = isNew && isDate(time) ? isLastMinute(time) : false
    // Don't let the end time overlap with any existing booking.
    const conflictsWithExistingBooking = findOverlappingCourt(bookingTime, time)
    // Don't allow booking to extend past END_HOUR
    const tooLate = !isSameDay(time, bookingTime) ? true : getHours(time) >= END_HOUR
    const allowed =
      !tooLate &&
      !conflictsWithExistingBooking &&
      (currentUser?.mayIgnoreFutureBookingLimit || !fallsWithinLastMinBookingWindow || isLastMinute(time))
    if (!allowed) {
      break
    }
    allowedEndTimes.push(i * SLOT_DURATION)
  }

  const validateRepeatEndDate = (selectedDate) => {
    const currentYear = getYear(getServerDate())
    // Must be before Dec 1
    const limit = set(getServerDate(), { year: currentYear, month: 11, date: 1 })
    return isBefore(selectedDate, limit)
  }

  const handleValuesChange = (changedValues, allValues) => {
    const {
      endTime,
      player1IsGuest,
      player2IsGuest,
      player3IsGuest,
      player4IsGuest,
      player1,
      player2,
      player3,
      player4,
    } = allValues
    if (!bookingToEdit) {
      setEndTime(add(bookingTime, { minutes: endTime }))
    }

    let numPlayers = 1

    if (player4?.id || player4IsGuest) {
      numPlayers = 4
    } else if (player3?.id || player3IsGuest) {
      numPlayers = 3
    } else if (player2?.id || player2IsGuest) {
      numPlayers = 2
    }
    setNumPlayers(numPlayers)
  }

  const handleOk = async () => {
    try {
      setSaving(true)

      const fields = { ...bookingToEdit, ...form.getFieldsValue(true) }

      if (isProgramOrEvent) {
        fields.showOwner = false
        fields.bookingType = 'Blocked'
        // fields.description = fields.comment
      }

      if (!currentUser?.mayBookMultiCourt) {
        fields.showOwner = true
      }
      const { repeatType } = fields
      fields.isRepeating = repeatType && repeatType !== REPEAT_NONE

      if (fields.isRepeating && !fields.repeatEndDate) {
        throw new Error('Repeat end date is required')
      }
      if (fields.repeatEndDate && !validateRepeatEndDate(fields.repeatEndDate)) {
        throw new Error('Repeat end date invalid')
      }

      let booking = await onOk(fields)
      // booking = { courts: fields.courts, ...booking }
      if (_.isNil(fields.id)) {
        // For now only create repeats for a new booking.
        //await handleSaveRepeats(booking)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    onCancel()
  }

  useEffect(() => {
    if (visible) {
      form.resetFields()
      setNumPlayers(1)
      setBallMachineChecked(false)
      setMatch(false)
      setProgramOrEvent(false)
      setAdminOptionsVisible(false)
      setReadonly(isNew ? false : bookingToEdit ? !userIsBookingOwner(bookingToEdit, currentUser) : false)

      if (bookingToEdit) {
        const startTime = parseDateTime(bookingToEdit.startTime)
        let endTime = parseDateTime(bookingToEdit.endTime)
        if (isBefore(endTime, startTime)) {
          // There used to be a bug where repeating bookings' end dates weren't set correctly.
          // Update them on the fly here.
          endTime = set(endTime, {
            year: getYear(startTime),
            month: getMonth(startTime),
            date: getDate(startTime),
          })
        }
        const diff = differenceInMinutes(endTime, startTime)
        setEndTime(diff)
        let repeatEndDate = bookingToEdit.repeatEndDate ? parseISO(bookingToEdit.repeatEndDate) : null

        if (!bookingToEdit.showOwner && bookingToEdit.bookingType === 'Blocked') {
          setProgramOrEvent(true)
          setAdminOptionsVisible(true)
          form.setFieldsValue({ programOrEvent: true })
        }

        const courtIds = _.map(bookingToEdit.courts, 'id')

        form.setFieldsValue({ ...bookingToEdit, endTime: diff, repeatEndDate, courts: courtIds })
        setMatch(bookingToEdit?.bookingType === 'Tournament')
        handleValuesChange([], bookingToEdit)
      } else {
        const player1 = _.includes(
          _.map(users, (u) => u.id),
          currentUser?.id,
        )
          ? currentUser
          : null

        // Default to "program or event" mode if the user is the event admin user.
        const programOrEvent = isEventAdminUser(currentUser)
        updateIsProgramOrEvent(programOrEvent)
        form.setFieldsValue({ bookingType: 'General', showOwner: true, player1, programOrEvent })
      }
    }
  }, [visible, form, bookingToEdit])

  const PlayerDisplay = ({ num }) => {
    const isGuest = form.getFieldValue(`player${num}IsGuest`)
    const guestName = form.getFieldValue(`guest${num}`)
    const player = form.getFieldValue(`player${num}`)
    const { firstName, lastName } = getNames(player)

    const displayName = isGuest
      ? `${guestName} (guest)`
      : firstName && lastName
        ? `${firstName} ${lastName}`
        : player?.username

    return (
      <Col span={24} key={num} style={{ marginBottom: '.5em' }}>
        • {displayName || 'Not specified'}
      </Col>
    )
  }

  const courts = [
    { label: 'Court 1', value: 1 },
    { label: 'Court 2', value: 2 },
    { label: 'Court 3', value: 3 },
  ]
  const repeatOptions = [
    { label: 'No repeat', value: REPEAT_NONE },
    { label: 'Repeat weekly', value: REPEAT_WEEKLY },
    { label: 'Repeat on weekdays', value: REPEAT_DAILY_WEEKDAYS },
    { label: 'Repeat daily', value: REPEAT_DAILY },
  ]

  const courtDesc = court ? `Court ${court} on ` : ''

  useEffect(() => {
    const checkBallMachineBooked = async (bookingTime) => {
      if (!bookingTime) {
        return
      }
      try {
        const ballMachineAvailable = isBallMachineAvailable(bookingTime)
        const ballMachineBooked = await isBallMachineBooked(bookingTime)
        const isCurrentTimeslotBookedForBallMachine = bookingToEdit?.bookingType === 'Ball-Machine'
        setCurrentTimeslotBookedForBallMachine(isCurrentTimeslotBookedForBallMachine)

        const ballMachineNote = isCurrentTimeslotBookedForBallMachine
          ? ''
          : !ballMachineAvailable
            ? '(not available at this time)'
            : ballMachineBooked
              ? '(already booked at this time)'
              : ''
        setBallMachineAvailable(ballMachineAvailable)
        setBallMachineBooked(ballMachineBooked)
        setBallMachineNote(ballMachineNote)
      } catch (e) {
        notifyError('error', e)
      }
    }
    checkBallMachineBooked(bookingTime)
  })

  const handlePurposeChanged = (e) => {
    handleBallMachineChecked(e)
    handleMatchChecked(e)
  }

  const handleBallMachineChecked = (e) => {
    setBallMachineChecked(e?.target?.value === 'Ball-Machine')
  }
  const handleMatchChecked = (e) => {
    setMatch(e?.target?.value === 'Tournament')
  }

  const bookingPurposeOptions = [
    { value: 'General', label: 'General use' },
    { value: 'Ladder', label: 'Ladder match' },
    { value: 'Lesson', label: 'Lesson' },
    { value: 'Tournament', label: 'Tournament match' },
    {
      value: 'Ball-Machine',
      label: 'Ball machine ' + ballMachineNote,
      disabled: isCurrentTimeslotBookedForBallMachine ? false : !ballMachineAvailable || ballMachineBooked,
    },
  ]

  if (currentUser?.mayBookMultiCourt) {
    bookingPurposeOptions.push({ value: 'Blocked', label: 'Blocked/closed' })
  }

  const bookingLimitNote =
    isNew && !currentUser?.mayBookMultiCourt && bookingTimeRemaining < maxBookingTimeMinutes
      ? `Your booking is limited to ${bookingTimeRemaining} minutes because you've already booked ${
          maxBookingTimeMinutes - bookingTimeRemaining
        } minutes of court time within the past ${BOOKING_PERIOD_HOURS} hours.`
      : ''

  const isRepeatingParent = bookingToEdit && bookingToEdit.isRepeating && !bookingToEdit.parent
  const isRepeatingChild = bookingToEdit && bookingToEdit.isRepeating && bookingToEdit.parent

  const title = readonly ? 'Booking: ' : 'Edit booking: '

  const buttonStyle = { marginBottom: '1em' }

  const bookingIdStyle = bp.xs
    ? { float: 'left', textAlign: 'left', width: '100%', display: 'block', marginBottom: '1em' }
    : { float: 'left' }

  const buttons = [
    <span key="info" style={{ float: 'left', color: 'gray', ...bookingIdStyle }}>
      {bookingToEdit && (
        <>
          Booking #{bookingToEdit.id} {bookingToEdit?.parent && <>(#{bookingToEdit?.parent?.id})</>}{' '}
        </>
      )}
    </span>,
  ]
  if (readonly && mayBeEdited(bookingToEdit, currentUser)) {
    buttons.push(
      <Button key="edit" type="link" onClick={handleEdit} style={buttonStyle}>
        Edit
      </Button>,
    )
  }
  buttons.push(
    <Button key="back" type={readonly ? 'primary' : 'link'} onClick={handleCancel} style={buttonStyle}>
      {readonly ? 'Close' : 'Cancel'}
    </Button>,
  )
  if (!readonly && !isNew) {
    buttons.push(
      <Button key="delete" type="danger" onClick={onDelete} loading={isSaving} style={buttonStyle}>
        {bp.xs ? 'Delete' : 'Delete booking'}
      </Button>,
    )
  }
  if (!readonly) {
    buttons.push(
      <Button key="submit" type="primary" onClick={handleOk} disabled={isSaving} loading={isSaving} style={buttonStyle}>
        {isSaving ? 'Saving' : isNew ? 'Save' : bp.xs ? 'Update' : 'Update booking'}
      </Button>,
    )
  }

  return (
    <Modal
      title={null}
      okButtonProps={{ type: 'primary' }}
      cancelButtonProps={{ type: 'link' }}
      onCancel={handleCancel}
      visible={visible}
      closable={true}
      maskClosable={readonly}
      keyboard={true}
      destroyOnClose={true}
      width={bp.xs ? '100%' : '700px'}
      footer={buttons}
    >
      <Paragraph className="bookingModalHeader" level={3}>
        {isNew ? (
          <>
            Create booking for <b>{formatDateTime(bookingTime, 'EEEE MMMM d')}</b>
          </>
        ) : readonly ? (
          <>
            <span>
              {courtDesc} {formatDateTime(bookingTime, 'MMM d')}
              <span style={{ paddingLeft: '1em', fontWeight: 'bold' }}>
                {formatDateTime(bookingTime, 'h:mma')} -{' '}
                {formatDateTime(add(bookingTime, { minutes: endTime }), 'h:mma')}
              </span>
            </span>
          </>
        ) : (
          <>
            <span>
              {title} {courtDesc} <b>{formatDateTime(bookingTime, 'MMM d')}</b> at{' '}
              <b>{formatDateTime(bookingTime, 'h:mm a')}</b>
            </span>
          </>
        )}
      </Paragraph>
      {isRepeatingChild && !readonly && (
        <Alert
          style={{
            padding: '.5em',
            textAlign: 'left',
            marginBottom: '1.5em',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
          type="warning"
          description={
            <>
              This is a repeating booking. You can edit this booking specifically, or edit the parent to change all
              related repeating bookings.{' '}
              <Button type="link" onClick={() => onOpenDifferentBooking(bookingToEdit.parent)}>
                Edit parent instead
              </Button>
            </>
          }
        />
      )}
      {isRepeatingParent && !readonly && (
        <Alert
          style={{
            padding: '.5em',
            textAlign: 'left',
            marginBottom: '1.5em',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
          type="warning"
          description={
            <>
              This is the parent of other repeating bookings. Editing some fields here (like comment/description and
              booking type) will update related repeating bookings. To change other fields (like start/end time, courts,
              etc) delete this and all repeating children and re-create the booking.
            </>
          }
        />
      )}
      {isNew && isLastMinuteBooking && !readonly && (
        <Alert
          style={{
            padding: '.5em',
            textAlign: 'center',
            marginBottom: '1.5em',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
          type="success"
          description={<>This is a last-minute booking and won't affect when you can book another court.</>}
        />
      )}
      {bookingLimitNote && !readonly && (
        <Alert style={{ marginBottom: '1em' }} type="warning" message={bookingLimitNote} />
      )}
      <Form
        name="bookingModal"
        form={form}
        size="small"
        layout="horizontal"
        colon={false}
        onValuesChange={handleValuesChange}
      >
        <Row gutter={10}>
          {!readonly && (
            <Col span={24}>
              <Form.Item label="When?">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{formatDateTime(bookingTime, 'h:mma')} -</span>
                  <Form.Item name="endTime" noStyle initialValue={DEFAULT_DURATION}>
                    <Select
                      disabled={!isNew}
                      style={{ width: '14em' }}
                      placeholder="Select end time"
                      options={_.map(allowedEndTimes, (minutes) => {
                        if (bookingTime) {
                          const h = minutes / 60
                          const endTime = h === 0.5 ? '30 minutes' : h === 1 ? '1 hour' : `${h} hours`
                          const displayText = `${formatDateTime(add(bookingTime, { minutes }), 'h:mma')} (${endTime})`
                          return { label: displayText, value: minutes }
                        }
                      })}
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            </Col>
          )}
          {!isProgramOrEvent && (
            <>
              {!readonly && (
                <Paragraph>
                  List players that will use this court.{' '}
                  <Text strong>Guests are permitted, but there is a limit of 3 visits per season per guest.</Text>
                </Paragraph>
              )}
              {readonly ? (
                <>
                  <PlayerDisplay num={1} />
                  {numPlayers >= 2 && <PlayerDisplay num={2} />}
                  {numPlayers >= 3 && <PlayerDisplay num={3} />}
                  {numPlayers >= 4 && <PlayerDisplay num={4} />}
                </>
              ) : (
                <>
                  <PlayerInput num={1} readonly={readonly} loading={loading} users={users} />
                  <PlayerInput num={2} readonly={readonly} loading={loading} users={users} />
                  <PlayerInput num={3} readonly={readonly} loading={loading} users={users} />
                  <PlayerInput num={4} readonly={readonly} loading={loading} users={users} />
                </>
              )}
            </>
          )}
          {isMatch && !readonly && (
            <Col span={24}>
              <Form.Item label="Result" name="result">
                <Input maxLength={100} placeholder="Match result (name and score - optional)" />
              </Form.Item>
            </Col>
          )}

          {isMatch && readonly && bookingToEdit?.result && (
            <Col span={24} style={{ marginTop: '10px', fontWeight: 'bold' }}>
              <Text style={{ fontSize: '1.2em' }}>{bookingToEdit?.result}</Text>
            </Col>
          )}
          {!isProgramOrEvent && (
            <>
              <Col span={24} style={{ marginTop: '10px' }}>
                {readonly && bookingToEdit?.comment ? (
                  <Text style={{ fontSize: '1.2em' }}>{bookingToEdit?.comment}</Text>
                ) : (
                  !readonly && (
                    <Form.Item label="Note" name="comment">
                      <Input
                        disabled={readonly}
                        maxLength={100}
                        placeholder={readonly ? '' : 'Optional note that will be displayed on your booking'}
                      />
                    </Form.Item>
                  )
                )}
              </Col>
              <Col span={24} style={{ marginTop: '10px' }}>
                {readonly && bookingToEdit?.bookingType !== 'General' ? (
                  <div style={{ textAlign: 'left' }}>
                    <Tag title={bookingToEdit?.bookingType} color={getTagColour(bookingToEdit?.bookingType)}>
                      {bookingToEdit?.bookingType}
                    </Tag>
                  </div>
                ) : (
                  !readonly && (
                    <Form.Item label="Purpose" name="bookingType">
                      <Radio.Group
                        disabled={readonly}
                        onChange={handlePurposeChanged}
                        options={bookingPurposeOptions}
                      />
                    </Form.Item>
                  )
                )}
              </Col>
            </>
          )}
          {ballMachineChecked && (
            <Col span={24}>
              <Alert
                style={{
                  padding: '.5em',
                  textAlign: 'center',
                  marginBottom: '1.5em',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
                type="info"
                description={<>Please e-transfer the $5 ball-machine rental fee to pay@hespelertennis.on.ca</>}
              />
            </Col>
          )}

          {!readonly && currentUser?.mayBookMultiCourt && (
            <>
              <div
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '0.5em',
                  marginTop: '1em',
                  border: '1px solid #bbb',
                  maxHeight: isProgramOrEvent ? '60vh' : '30vh',
                  overflow: 'auto',
                  width: '100%',
                }}
              >
                <Title
                  onClick={() => setAdminOptionsVisible((visible) => !visible)}
                  level={2}
                  style={{ marginTop: 0, marginBottom: '0em', cursor: 'pointer' }}
                >
                  <span style={{ paddingRight: '0.5em' }}>
                    {adminOptionsVisible ? <CollapseIcon /> : <ExpandIcon />}
                  </span>
                  Admin options{' '}
                </Title>

                <div style={{ display: adminOptionsVisible ? 'block' : 'none' }}>
                  <Col span={24} style={{ marginTop: '1em' }}>
                    <Paragraph style={{ fontSize: '0.95em' }}>
                      <Form.Item
                        name="programOrEvent"
                        valuePropName="checked"
                        initialValue={false}
                        style={{ margin: 0 }}
                        label={
                          <Title level={3} className="noPad">
                            Is this a program or event?
                          </Title>
                        }
                      >
                        <Checkbox onChange={(e) => updateIsProgramOrEvent(e.target.checked)} />
                      </Form.Item>
                      If checked, the booking owner will be hidden and the booking will be displayed consistently way
                      with other programs and events (vs personal bookings)
                    </Paragraph>

                    {isProgramOrEvent && (
                      <Form.Item label="" name="description">
                        <Input maxLength={100} placeholder="Program/event name" />
                      </Form.Item>
                    )}
                  </Col>

                  <Col span={24}>
                    <Title level={3} className="noPad">
                      Multiple courts
                    </Title>
                    <Paragraph style={{ fontSize: '0.95em' }}>
                      You can select multiple courts for this booking.{' '}
                      <b>Please ensure there are no existing bookings for the additional courts you choose here.</b>
                    </Paragraph>
                    <Form.Item label="" name="courts">
                      <Checkbox.Group options={courts} />
                    </Form.Item>
                  </Col>

                  {currentUser?.mayIgnoreFutureBookingLimit && (
                    <Col span={24}>
                      <Title level={3} className="noPad">
                        Repeat booking
                      </Title>
                      {isRepeatingChild || isRepeatingParent ? (
                        <Paragraph>
                          To change repeat booking options, delete the repeating booking and re-create.
                        </Paragraph>
                      ) : (
                        bookingToEdit && (
                          <Paragraph>Repeat booking options can only be changed when adding a new booking.</Paragraph>
                        )
                      )}
                      <Paragraph>
                        <Form.Item label="" name="repeatType" initialValue={'none'}>
                          <Radio.Group
                            className="repeatBookingOptions"
                            options={repeatOptions}
                            disabled={bookingToEdit || isRepeatingChild || isRepeatingParent}
                          />
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.repeatType !== currentValues.repeatType
                          }
                        >
                          {({ getFieldValue }) => {
                            const isRequired = getFieldValue('repeatType') !== 'none'

                            return (
                              <Form.Item
                                name="repeatEndDate"
                                label="Repeat end date"
                                rules={[
                                  {
                                    required: isRequired,
                                    message: 'Please select an end date for the recurring booking',
                                  },
                                  {
                                    validator: async (_, selectedDate) => {
                                      if (!selectedDate) return Promise.resolve()
                                      if (!validateRepeatEndDate(selectedDate)) {
                                        return Promise.reject(new Error(`Date must be before December 1`))
                                      } else {
                                        return Promise.resolve()
                                      }
                                    },
                                  },
                                ]}
                              >
                                <CustomDatePicker
                                  disabled={!isRequired || bookingToEdit || isRepeatingChild || isRepeatingParent}
                                />
                              </Form.Item>
                            )
                          }}
                        </Form.Item>
                      </Paragraph>
                    </Col>
                  )}

                  {!isProgramOrEvent && (
                    <Col span={24}>
                      <Paragraph style={{ fontSize: '0.95em' }}>
                        <Form.Item
                          style={{ margin: 0 }}
                          label={
                            <Title level={3} className="noPad">
                              Show booking owner
                            </Title>
                          }
                          name="showOwner"
                          valuePropName="checked"
                          initialValue={true}
                        >
                          <Checkbox />
                        </Form.Item>
                        Uncheck to hide the booking owner (for events, programming, etc)
                      </Paragraph>
                    </Col>
                  )}

                  {!isProgramOrEvent && currentUser?.mayEditAnyBooking && (
                    <Col span={24}>
                      <Paragraph style={{ fontSize: '0.95em' }}>
                        <Form.Item
                          shouldUpdate
                          name={[`createdByUser`, 'id']}
                          label={
                            <Title level={3} className="noPad">
                              Specify different booking owner
                            </Title>
                          }
                          style={{ margin: 0 }}
                        >
                          <Select
                            placeholder="Select player (type to search)"
                            notFoundContent="Not found"
                            allowClear
                            showSearch
                            loading={loading}
                            options={_.map(users, (u) => ({
                              label: `${_.trim(u.lastName)}, ${_.trim(u.firstName)}`,
                              value: u.id,
                            }))}
                            optionFilterProp="label"
                            filterSort={(optionA, optionB) =>
                              (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                            }
                          />
                        </Form.Item>
                        By selecting a different owner here, the booking will appear as though it were booked by that
                        person, not you.
                      </Paragraph>
                    </Col>
                  )}
                </div>
              </div>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  )
}

export default BookingModal
